'use client'

import { useState } from 'react'

// "Stand up demo fleet" card above the Clients list — one click seeds the
// 11 niche templates + 11 demo tenants (full vertical profiles, personas,
// KBs) and provisions each through the real saga. Idempotent: re-running
// skips everything that exists. Under TELNYX_STUB=1 the whole fleet stands
// up with zero carrier spend.

const buttonStyle = (disabled) => ({
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid var(--theme-elevation-250)',
  background: disabled ? 'var(--theme-elevation-100)' : 'var(--theme-elevation-0)',
  color: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13,
})

export function DemoFleetPanel() {
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')

  const run = async () => {
    setBusy(true)
    setError('')
    setReport(null)
    try {
      const res = await fetch('/api/fulfillment/demo-up', {
        method: 'POST',
        credentials: 'include',
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(json.error || 'Demo fleet setup failed')
        return
      }
      setReport(json)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        border: '1px solid var(--theme-elevation-150)',
        borderRadius: 8,
        padding: '14px 16px',
        background: 'var(--theme-elevation-50)',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Demo fleet</h3>
        <button type="button" disabled={busy} style={buttonStyle(busy)} onClick={run}>
          {busy ? 'Standing up fleet… (runs 11 provisions, ~a minute)' : 'Stand up demo fleet'}
        </button>
        <button
          type="button"
          disabled={busy}
          style={buttonStyle(busy)}
          onClick={async () => {
            // Overwrites hand-edited template prompts — make that explicit.
            if (
              !window.confirm(
                'Refresh all 11 niche templates from code? This OVERWRITES the greeting and instructions (including hand edits) with the latest versions — retention plays included. Tool manifests, model, and voice are kept. Existing clients also need "Re-sync assistant" afterward.',
              )
            ) {
              return
            }
            setBusy(true)
            setError('')
            try {
              const res = await fetch('/api/fulfillment/refresh-templates', {
                method: 'POST',
                credentials: 'include',
              })
              const json = await res.json().catch(() => ({}))
              if (!res.ok) setError(json.error || 'Template refresh failed')
              else setError('')
            } finally {
              setBusy(false)
            }
          }}
        >
          Refresh templates from code
        </button>
        <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>
          Stand-up seeds + provisions all 11 niche demos (re-runs skip existing). Refresh pushes the
          latest prompts onto existing templates.
        </span>
      </div>
      {error && (
        <p style={{ color: 'var(--theme-error-500, #b00020)', fontSize: 13, margin: '10px 0 0' }}>{error}</p>
      )}
      {report && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <p style={{ margin: 0, color: 'var(--theme-elevation-500)' }}>
            {report.stub ? 'STUB carrier (zero spend). ' : 'LIVE carrier. '}
            Templates created: {report.templatesCreated} · tenants created: {report.tenantsCreated}
          </p>
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'grid', gap: 4 }}>
            {(report.provisioned || []).map((row) => (
              <li key={row.slug} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span>{row.status === 'complete' ? '✅' : '⚠️'}</span>
                <span style={{ minWidth: 180 }}>{row.name}</span>
                <span style={{ fontFamily: 'monospace' }}>{row.phoneNumber || '—'}</span>
                <a href={`/s/${row.slug}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>
                  site
                </a>
                {row.failedSteps.length > 0 && (
                  <span style={{ color: 'var(--theme-warning-500, #b26a00)' }}>
                    failed: {row.failedSteps.join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
